import { createSelector } from '@reduxjs/toolkit';
import { RootState } from './store';

// Basic selectors
export const selectUserState = (state: RootState) => state.user;
export const selectCustomerState = (state: RootState) => state.customer;
export const selectSiteState = (state: RootState) => state.site;
export const selectLocationState = (state: RootState) => state.location;
export const selectSelectionState = (state: RootState) => state.selection;

// User selectors
export const selectUser = createSelector(
  [selectUserState],
  (userState) => userState.data
);

export const selectUserLoading = createSelector(
  [selectUserState],
  (userState) => userState.loading
);

export const selectUserError = createSelector(
  [selectUserState],
  (userState) => userState.error
);

export const selectUserId = createSelector(
  [selectUser],
  (user) => user?.id || null
);

// Customer selectors
export const selectCustomers = createSelector(
  [selectCustomerState],
  (customerState) => customerState.data
);

export const selectCustomerLoading = createSelector(
  [selectCustomerState],
  (customerState) => customerState.loading
);

export const selectCustomerError = createSelector(
  [selectCustomerState],
  (customerState) => customerState.error
);

// Site selectors
export const selectSites = createSelector(
  [selectSiteState],
  (siteState) => siteState.data
);

export const selectSiteLoading = createSelector(
  [selectSiteState],
  (siteState) => siteState.loading
);

export const selectSiteError = createSelector(
  [selectSiteState],
  (siteState) => siteState.error
);

// Location selectors
export const selectLocations = createSelector(
  [selectLocationState],
  (locationState) => locationState.data
);

export const selectLocationLoading = createSelector(
  [selectLocationState],
  (locationState) => locationState.loading
);

export const selectLocationError = createSelector(
  [selectLocationState],
  (locationState) => locationState.error
);

// Selection selectors
export const selectSelectedCustomerType = createSelector(
  [selectSelectionState],
  (selectionState) => selectionState.selectedType
);

export const selectSelectedCustomerId = createSelector(
  [selectSelectionState],
  (selectionState) => selectionState.selectedCustomerId
);

export const selectSelectedSiteId = createSelector(
  [selectSelectionState],
  (selectionState) => selectionState.selectedSiteId
);

export const selectSelectedSiteName = createSelector(
  [selectSelectionState],
  (selectionState) => selectionState.selectedSiteName
);

export const selectSelectedGDM = createSelector(
  [selectSelectionState],
  (selectionState) => selectionState.selectedGDM
);

export const selectSelectedLocationId = createSelector(
  [selectSelectionState],
  (selectionState) => selectionState.selectedLocationId
);

export const selectSelectedLocationName = createSelector(
  [selectSelectionState],
  (selectionState) => selectionState.selectedLocationName
);

export const selectInitialLoad = createSelector(
  [selectSelectionState],
  (selectionState) => selectionState.initialLoad
);

// Combined selectors - for easy access to selected objects
export const selectSelectedCustomer = createSelector(
  [selectCustomers, selectSelectedCustomerId],
  (customers, selectedId) => {
    if (!customers || !selectedId) return null;
    const customerList = Array.isArray(customers) ? customers : [customers];
    return customerList.find(c => c.id === selectedId) || null;
  }
);

export const selectSelectedSite = createSelector(
  [selectSites, selectSelectedSiteId],
  (sites, selectedId) => {
    if (!sites || !selectedId) return null;
    return Array.isArray(sites) ? sites.find((s: any) => s.id === selectedId) || null : null;
  }
);

export const selectSelectedLocation = createSelector(
  [selectLocations, selectSelectedLocationId],
  (locations, selectedId) => {
    if (!locations || !selectedId) return null;
    return locations.find((l: any) => l.id === selectedId) || null;
  }
);

// Convenience selector for current context
export const selectCurrentContext = createSelector(
  [selectSelectedCustomer, selectSelectedSite, selectSelectedLocation],
  (customer, site, location) => ({
    customer,
    site,
    location,
    hasCustomer: !!customer,
    hasSite: !!site,
    hasLocation: !!location,
  })
);

// Smart selector: Uses stored object first (persists across pagination), 
// falls back to finding in current list (backward compatibility)
export const selectSelectedSiteObject = createSelector(
  [selectSelectionState, selectSites, selectSelectedSiteId],
  (selectionState, sites, selectedId) => {
    // First: Check if we have a stored site object (persists across page changes)
    if (selectionState.selectedSiteObject) {
      return selectionState.selectedSiteObject;
    }
    
    // Fallback: Try to find in current paginated list
    if (!sites || !selectedId) return null;

    // Handle both array and object with 'sites' property safely
    let siteList: any[] = [];
    if (Array.isArray(sites)) {
      siteList = sites;
    } else if (sites && typeof sites === 'object' && Array.isArray((sites as any).sites)) {
      siteList = (sites as any).sites;
    }

    return siteList.find(
      (s: any) => String(s.site_id) === String(selectedId)
    ) || null;
  }
);